// Client-side chess validation engine
// This provides immediate feedback without waiting for server validation
import type { 
  Position, 
  Piece
} from '@shared/types/chess.types';
import type { GameState } from '@shared/types/game.types';

export class ChessEngine {
  static positionToKey(position: Position): string {
    return `${position.file}${position.rank}`;
  }
  
  static keyToPosition(key: string): Position {
    return {
      file: key[0] as Position['file'],
      rank: parseInt(key[1]) as Position['rank'],
    };
  }
  
  static isValidPosition(position: Position): boolean {
    const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    const ranks = [1, 2, 3, 4, 5, 6, 7, 8];
    return files.includes(position.file) && ranks.includes(position.rank);
  }
  
  static getPossibleMoves(
    gameState: GameState,
    position: Position
  ): Position[] {
    console.log('[ChessEngine] getPossibleMoves called for position:', position);
    console.log('[ChessEngine] board.squares type:', typeof gameState.board.squares);
    console.log('[ChessEngine] board.squares is Map?', gameState.board.squares instanceof Map);
    console.log('[ChessEngine] board.squares has .get?', typeof gameState.board.squares.get === 'function');
    
    const piece = gameState.board.squares.get(this.positionToKey(position));
    if (!piece || piece.color !== gameState.currentTurn) {
      console.log('[ChessEngine] No piece or wrong turn. Piece:', piece, 'Current turn:', gameState.currentTurn);
      return [];
    }
    
    switch (piece.type) {
      case 'pawn':
        return this.getPawnMoves(gameState, position, piece);
      case 'knight':
        return this.getKnightMoves(gameState, position, piece);
      case 'bishop':
        return this.getBishopMoves(gameState, position, piece);
      case 'rook':
        return this.getRookMoves(gameState, position, piece);
      case 'queen':
        return this.getQueenMoves(gameState, position, piece);
      case 'king':
        return this.getKingMoves(gameState, position, piece);
      default:
        return [];
    }
  }
  
  private static getPawnMoves(
    gameState: GameState,
    position: Position,
    piece: Piece
  ): Position[] {
    const moves: Position[] = [];
    const direction = piece.color === 'white' ? 1 : -1;
    const startRank = piece.color === 'white' ? 2 : 7;
    
    // One square forward
    const oneForward: Position = {
      file: position.file,
      rank: (position.rank + direction) as Position['rank'],
    };
    
    console.log('[ChessEngine] getPawnMoves - checking oneForward:', oneForward);
    console.log('[ChessEngine] board.squares has .has method?', typeof gameState.board.squares.has === 'function');
    
    const oneForwardKey = this.positionToKey(oneForward);
    const isValidPos = this.isValidPosition(oneForward);
    const hasSquare = gameState.board.squares.has(oneForwardKey);
    const squareValue = gameState.board.squares.get(oneForwardKey);
    console.log('[ChessEngine] oneForwardKey:', oneForwardKey, 'isValidPos:', isValidPos, 'hasSquare:', hasSquare, 'squareValue:', squareValue);
    
    if (
      isValidPos &&
      !squareValue  // Check if square is empty (null/undefined) rather than if key exists
    ) {
      moves.push(oneForward);
      
      // Two squares forward from starting position
      if (position.rank === startRank) {
        const twoForward: Position = {
          file: position.file,
          rank: (position.rank + direction * 2) as Position['rank'],
        };
        
        if (!gameState.board.squares.get(this.positionToKey(twoForward))) {
          moves.push(twoForward);
        }
      }
    }
    
    // Captures
    const fileIndex = 'abcdefgh'.indexOf(position.file);
    for (const fileDelta of [-1, 1]) {
      const targetFile = 'abcdefgh'[fileIndex + fileDelta] as Position['file'];
      if (!targetFile) continue;
      
      const capturePosition: Position = {
        file: targetFile,
        rank: (position.rank + direction) as Position['rank'],
      };
      
      if (this.isValidPosition(capturePosition)) {
        const targetPiece = gameState.board.squares.get(
          this.positionToKey(capturePosition)
        );
        
        // Regular capture or en passant
        if (
          (targetPiece && targetPiece.color !== piece.color) ||
          (gameState.enPassantTarget &&
            capturePosition.file === gameState.enPassantTarget.file &&
            capturePosition.rank === gameState.enPassantTarget.rank)
        ) {
          moves.push(capturePosition);
        }
      }
    }
    
    return moves;
  }
  
  private static getKnightMoves(
    gameState: GameState,
    position: Position,
    piece: Piece
  ): Position[] {
    const moves: Position[] = [];
    const deltas = [
      [-2, -1], [-2, 1], [-1, -2], [-1, 2],
      [1, -2], [1, 2], [2, -1], [2, 1],
    ];
    
    const fileIndex = 'abcdefgh'.indexOf(position.file);
    
    for (const [rankDelta, fileDelta] of deltas) {
      const targetFile = 'abcdefgh'[fileIndex + fileDelta] as Position['file'];
      const targetRank = (position.rank + rankDelta) as Position['rank'];
      
      if (!targetFile || targetRank < 1 || targetRank > 8) continue;
      
      const targetPosition: Position = { file: targetFile, rank: targetRank };
      const targetPiece = gameState.board.squares.get(
        this.positionToKey(targetPosition)
      );
      
      if (!targetPiece || targetPiece.color !== piece.color) {
        moves.push(targetPosition);
      }
    }
    
    return moves;
  }
  
  private static getSlidingMoves(
    gameState: GameState,
    position: Position,
    piece: Piece,
    directions: number[][]
  ): Position[] {
    const moves: Position[] = [];
    const fileIndex = 'abcdefgh'.indexOf(position.file);
    
    for (const [rankDelta, fileDelta] of directions) {
      for (let distance = 1; distance < 8; distance++) {
        const targetFile = 'abcdefgh'[fileIndex + fileDelta * distance] as Position['file'];
        const targetRank = (position.rank + rankDelta * distance) as Position['rank'];
        
        if (!targetFile || targetRank < 1 || targetRank > 8) break;
        
        const targetPosition: Position = { file: targetFile, rank: targetRank };
        const targetPiece = gameState.board.squares.get(
          this.positionToKey(targetPosition)
        );
        
        if (!targetPiece) {
          moves.push(targetPosition);
        } else {
          if (targetPiece.color !== piece.color) {
            moves.push(targetPosition);
          }
          break;
        }
      }
    }
    
    return moves;
  }
  
  private static getBishopMoves(
    gameState: GameState,
    position: Position,
    piece: Piece
  ): Position[] {
    const diagonalDirections = [
      [-1, -1], [-1, 1], [1, -1], [1, 1],
    ];
    return this.getSlidingMoves(gameState, position, piece, diagonalDirections);
  }
  
  private static getRookMoves(
    gameState: GameState,
    position: Position,
    piece: Piece
  ): Position[] {
    const straightDirections = [
      [-1, 0], [1, 0], [0, -1], [0, 1],
    ];
    return this.getSlidingMoves(gameState, position, piece, straightDirections);
  }
  
  private static getQueenMoves(
    gameState: GameState,
    position: Position,
    piece: Piece
  ): Position[] {
    const allDirections = [
      [-1, -1], [-1, 0], [-1, 1],
      [0, -1],           [0, 1],
      [1, -1], [1, 0], [1, 1],
    ];
    return this.getSlidingMoves(gameState, position, piece, allDirections);
  }
  
  private static getKingMoves(
    gameState: GameState,
    position: Position,
    piece: Piece
  ): Position[] {
    const moves: Position[] = [];
    const fileIndex = 'abcdefgh'.indexOf(position.file);
    
    // Regular moves
    for (let rankDelta = -1; rankDelta <= 1; rankDelta++) {
      for (let fileDelta = -1; fileDelta <= 1; fileDelta++) {
        if (rankDelta === 0 && fileDelta === 0) continue;
        
        const targetFile = 'abcdefgh'[fileIndex + fileDelta] as Position['file'];
        const targetRank = (position.rank + rankDelta) as Position['rank'];
        
        if (!targetFile || targetRank < 1 || targetRank > 8) continue;
        
        const targetPosition: Position = { file: targetFile, rank: targetRank };
        const targetPiece = gameState.board.squares.get(
          this.positionToKey(targetPosition)
        );
        
        if (!targetPiece || targetPiece.color !== piece.color) {
          moves.push(targetPosition);
        }
      }
    }
    
    // Castling
    if (!piece.hasMoved && !gameState.inCheck) {
      const rank = piece.color === 'white' ? 1 : 8;
      const rights = gameState.castlingRights[piece.color];
      
      // Kingside
      if (rights.kingside) {
        const rookKey = `h${rank}`;
        const rook = gameState.board.squares.get(rookKey);
        
        if (rook && !rook.hasMoved) {
          const betweenSquares = [`f${rank}`, `g${rank}`];
          const allEmpty = betweenSquares.every(
            (key) => !gameState.board.squares.get(key)
          );
          
          if (allEmpty) {
            moves.push({ file: 'g', rank } as Position);
          }
        }
      }
      
      // Queenside
      if (rights.queenside) {
        const rookKey = `a${rank}`;
        const rook = gameState.board.squares.get(rookKey);
        
        if (rook && !rook.hasMoved) {
          const betweenSquares = [`b${rank}`, `c${rank}`, `d${rank}`];
          const allEmpty = betweenSquares.every(
            (key) => !gameState.board.squares.get(key)
          );
          
          if (allEmpty) {
            moves.push({ file: 'c', rank } as Position);
          }
        }
      }
    }
    
    return moves;
  }
}